import { ScheduleState, TimeRange, Weekday } from '@/entities/campaign/types';
import { WEEKDAYS } from '../constants';

/**
 * Validates a time string format HH:mm
 */
const isValidTimeFormat = (time: string): boolean => {
  return /^(?:([0-1]?[0-9]|2[0-3]):[0-5][0-9]|24:00)$/.test(time);
};

/**
 * Converts HH:mm string to minutes since midnight for easy comparison
 */
const timeToMinutes = (time: string): number => {
  if (!isValidTimeFormat(time)) return 0;
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
};

/**
 * Ensures start and end times are not identical
 */
export const hasZeroDurationRanges = (ranges: TimeRange[]): boolean => {
  return ranges.some(range => {
    if (!range.start || !range.end) return false;
    return timeToMinutes(range.end) === timeToMinutes(range.start);
  });
};

/**
 * Checks for exactly identical ranges
 */
export const hasDuplicateRanges = (ranges: TimeRange[]): boolean => {
  const seen = new Set<string>();
  for (const range of ranges) {
    if (!range.start || !range.end) continue;
    const key = `${range.start}-${range.end}`;
    if (seen.has(key)) return true;
    seen.add(key);
  }
  return false;
};

/**
 * Sorts time ranges chronologically by start time
 */
export const sortTimeRanges = (ranges: TimeRange[]): TimeRange[] => {
  return [...ranges].sort((a, b) => {
    const aEmpty = !a.start;
    const bEmpty = !b.start;

    if (aEmpty && bEmpty) return 0;
    if (aEmpty) return 1;
    if (bEmpty) return -1;

    return timeToMinutes(a.start) - timeToMinutes(b.start);
  });
};

/**
 * Checks for overlapping ranges (after sorting and splitting overnight ranges)
 */
export const hasOverlappingRanges = (ranges: TimeRange[]): boolean => {
  const intervals: { start: number; end: number }[] = [];

  ranges
    .filter(r => r.start && r.end)
    .forEach(r => {
      const start = timeToMinutes(r.start);
      const end = timeToMinutes(r.end);

      if (end < start) {
        // Overnight slot, split into two intervals
        intervals.push({ start, end: 24 * 60 });
        if (end > 0) {
          intervals.push({ start: 0, end });
        }
      } else {
        intervals.push({ start, end });
      }
    });

  // Sort by start time
  intervals.sort((a, b) => a.start - b.start);

  for (let i = 0; i < intervals.length - 1; i++) {
    if (intervals[i].end > intervals[i + 1].start) {
      return true; // Overlap detected
    }
  }
  return false;
};

/**
 * Validates the entire ranges array for limits and basic errors
 */
export const validateTimeRanges = (ranges: TimeRange[]): string | null => {
  if (!ranges || ranges.length === 0)
    return 'At least one time slot is required.';
  if (ranges.length > 10) return 'Maximum of 10 time slots allowed per day';

  /* if (ranges.some(r => !r.start || !r.end)) {
    return 'All time slots must have both start and end times';
  }*/

  if (hasZeroDurationRanges(ranges)) {
    return 'Start and end times cannot be identical.';
  }

  if (hasDuplicateRanges(ranges)) {
    return 'Duplicate time slots are not allowed.';
  }

  if (hasOverlappingRanges(ranges)) {
    return 'Time slots cannot overlap.';
  }

  return null;
};

/**
 * Cleans the scheduling payload before sending to the backend.
 * Strips disabled sections and removes empty ranges.
 */
export const sanitizeSchedulingPayload = (
  scheduling?: ScheduleState
): ScheduleState | undefined => {
  if (!scheduling) return undefined;

  const sanitized: ScheduleState = {
    enableDayParting: scheduling.enableDayParting,
    enableTimeParting: scheduling.enableTimeParting,
    selectedDays: scheduling.enableDayParting
      ? [...(scheduling.selectedDays || [])]
      : [],
    allDays: [],
    perDaySchedules: {},
  };

  if (scheduling.enableTimeParting) {
    // Sanitize allDays
    sanitized.allDays = sortTimeRanges(
      (scheduling.allDays || []).filter(r => r.start && r.end)
    );

    // Sanitize perDaySchedules
    if (scheduling.perDaySchedules && sanitized.selectedDays.length > 0) {
      WEEKDAYS.forEach(day => {
        const dayConfig = scheduling.perDaySchedules[day.id as Weekday];
        if (
          sanitized.selectedDays.includes(day.id as Weekday) &&
          dayConfig &&
          dayConfig.enabled
        ) {
          const validRanges = sortTimeRanges(
            (dayConfig.ranges || []).filter(r => r.start && r.end)
          );
          if (validRanges.length > 0) {
            sanitized.perDaySchedules[day.id as Weekday] = {
              enabled: true,
              ranges: validRanges,
            };
          }
        }
      });
    }
  }

  return sanitized;
};

/**
 * Generates a determinisitc summary badge text
 */
export const generateScheduleSummary = (scheduling?: ScheduleState): string => {
  if (!scheduling) return 'Full Week • Full Day';

  const isFullWeek =
    !scheduling.enableDayParting ||
    (scheduling.selectedDays || []).length === 7;
  const daysText = isFullWeek
    ? 'Full Week'
    : `${(scheduling.selectedDays || []).length} Days`;

  if (!scheduling.enableTimeParting) {
    return `${daysText} • Full Day`;
  }

  let totalRanges = 0;
  let customDays = 0;

  const activeDays = scheduling.enableDayParting
    ? scheduling.selectedDays || []
    : WEEKDAYS.map(day => day.id);

  // If there are no ranges configured at all (even in allDays), treat as Full Day
  const hasAllDaysRanges =
    (scheduling.allDays || []).filter(r => r.start && r.end).length > 0;
  let hasAnyCustomRanges = hasAllDaysRanges;

  activeDays.forEach(day => {
    const daySchedule = scheduling.perDaySchedules?.[day as Weekday];
    if (daySchedule?.enabled) {
      const validRanges = (daySchedule.ranges || []).filter(
        r => r.start && r.end
      );
      if (validRanges.length > 0) {
        customDays++;
        totalRanges += validRanges.length;
        hasAnyCustomRanges = true;
      }
    } else if (hasAllDaysRanges) {
      totalRanges += (scheduling.allDays || []).filter(
        r => r.start && r.end
      ).length;
    }
  });

  if (!hasAnyCustomRanges) {
    return `${daysText} • Full Day`;
  }

  return `${daysText} • Custom Times (${totalRanges} ranges)`;
};
